// 9fbef606107a605d69c0edbcd8029e5d
import isMatch from 'date-fns/isMatch';
import isFuture from 'date-fns/isFuture';
import format from 'date-fns/format';
import { convertToValidDate } from '@tcp/core/src/utils/parseDate';
import { getIsInternationalPhoneNumber } from '@tcp/core/src/reduxStore/selectors/session.selectors';
import { isCanada, getDateObject } from '../index';
import { CARD_TYPES } from '../../components/features/CnC/SinglePageCheckout/container/SinglePageCheckout.constants';
import { convertDateWithSlash } from '../DateUtils';
import { getStoreRef } from '../store.utils';

const ACCEPTED_CREDIT_CARDS = {
  AMEX: 'AMEX',
};

let emailValidationRegex = '';

function requiredValidator(value, isRequired) {
  return !isRequired || (value || '').toString().length > 0;
}

function nonEmptyValidator(value, isRequired) {
  return !isRequired || (value.trim() || '').toString().length > 0;
}

function minLengthValidator(value, minLength) {
  return (value || '').length >= minLength;
}

function maxLengthValidator(value, maxLength) {
  return (value || '').length <= maxLength;
}

// eslint-disable-next-line complexity
function phoneValidator(value, param, linkedProps, linkedFields) {
  const storeRef = getStoreRef();
  const state = (storeRef && storeRef.getState()) || {};
  const isPhoneNumberFlagComponentDisabled = linkedFields?.[0] === undefined;
  const isInternationalPhoneNumberEnabled = getIsInternationalPhoneNumber(state);

  const isUsOrCaNumber = linkedFields?.[0] === '+1';
  const isInternationalNumber =
    !isPhoneNumberFlagComponentDisabled && isInternationalPhoneNumberEnabled && !isUsOrCaNumber;
  if ((value || '').length === 0) {
    return true; // otherwise it's always mandatory
  }

  if (
    isInternationalNumber &&
    (/[^0-9\s\-()]+/.test(value) || // Rejects any characters other than digits, spaces, hyphens, and parentheses
      /\s{2,}/.test(value) || // Rejects multiple consecutive spaces
      /-{2,}/.test(value) || // Rejects multiple consecutive hyphens
      /\(\s*\)/.test(value)) // Rejects empty parentheses or parentheses with only spaces
  ) {
    return false;
  }

  const digitsOnly = value?.replace(/\D/g, '');
  let validFormat;

  if (!isInternationalPhoneNumberEnabled || isPhoneNumberFlagComponentDisabled) {
    validFormat = /^(1-?)?(\([2-9]\d{2}\)|[2-9]\d{2})[- ]?[2-9]\d{2}-?\d{4}$/.test(value);
  } else if (isUsOrCaNumber) {
    validFormat =
      /^(1[ -]?)?(\([2-9]\d{2}\)|[2-9]\d{2})[ -]?[2-9]\d{2}[ -]?\d{4}$/.test(value) ||
      /^[2-9]\d{4} [2-9]\d{4}$/.test(value) ||
      /^\d{4} \d{3} \d{3}$/.test(value);
  } else {
    validFormat = /^\d{4,12}$/.test(digitsOnly);
  }

  if (!validFormat) {
    return false;
  }

  const numbersOnly = value.replace(/\D/g, '');
  const isInvalid =
    /^(\d)\1\1\1\1\1\1\1\1\1$/gi.test(numbersOnly) ||
    '0123456789012345678'.indexOf(value) > -1 ||
    '9876543210987654321'.indexOf(numbersOnly) > -1;
  return !isInvalid;
}

function addressValidator(value) {
  return /^[0-9a-zA-Z '#.\-,]*$/.test(value);
}

function addressField1Validation(value, param, linkedProps, linkedFields) {
  let country;
  if (linkedFields?.[0] === 'US') {
    country = 'US';
  } else if (linkedFields?.[0] === 'CA') {
    country = 'CA';
  } else {
    country = 'INT'; // International country
  }

  if (country === 'INT') {
    return /^(?=.*[a-zA-Z]).*$/.test(value);
  }
  return /(?=.*\d)(?=.*[a-zA-Z])/.test(value);
}

function zipcodeValidator(valueParam, param, linkedProps, linkedFields) {
  const value = (valueParam || '').toUpperCase();

  let country;
  if (linkedFields?.[0] === 'US') {
    country = 'US';
  } else if (linkedFields?.[0] === 'CA') {
    country = 'CA';
  } else {
    country = 'INT'; // International country
  }
  return (
    (country === 'US' && /^\d{5}-\d{4}$|^\d{5}$/.test(value) && value.substr(0, 5) !== '00000') ||
    (country === 'CA' &&
      /^[ABCEGHJKLMNPRSTVXY]{1}\d{1}[A-Z]{1} *\d{1}[A-Z]{1}\d{1}$/.test(value)) ||
    (country === 'INT' && /^[A-Z0-9 ]+$/.test(value)) // Allows alphabets, numbers, and spaces for international zip codes
  );
}

function postalcodeValidator(valueParam) {
  const value = (valueParam || '').toUpperCase();
  const country = isCanada() ? 'CA' : 'US';
  return (
    (country === 'US' && /^\d{5}-\d{4}$|^\d{5}$/.test(value) && value.substr(0, 5) !== '00000') ||
    (country === 'CA' && /^[ABCEGHJKLMNPRSTVXY]{1}\d{1}[A-Z]{1} *\d{1}[A-Z]{1}\d{1}$/.test(value))
  );
}

function noCountryZipValidator(valueParam) {
  let validZip;
  const value = (valueParam || '').toUpperCase();
  if (/^\d{5}-\d{4}$|^\d{5}$/.test(value) && value.substr(0, 5) !== '00000') validZip = true;
  else if (/^[ABCEGHJKLMNPRSTVXY]{1}\d{1}[A-Z]{1} *\d{1}[A-Z]{1}\d{1}$/.test(value))
    validZip = true;
  else validZip = false;
  return validZip;
}

function noCountryZipMergeValidator(valueParam) {
  let validZip;
  const value = (valueParam || '').toUpperCase();
  if (/^\d{5}-\d{4}$|^\d{5}$|^\d{6,15}$/.test(value) && value.substr(0, 5) !== '00000')
    validZip = true;
  else if (/^[ABCEGHJKLMNPRSTVXY]{1}\d{1}[A-Z]{1} *\d{1}[A-Z]{1}\d{1}$/.test(value))
    validZip = true;
  else validZip = false;
  return validZip;
}

function nameValidator(value) {
  return /^[a-zA-Z áéíóúÁÉÍÓÚäëïöüÄËÏÖÜñÑ]*$/.test(value);
}

function cityNameValidator(value) {
  return /^[a-zA-Z áéíóúÁÉÍÓÚäëïöüÄËÏÖÜñÑ '#.-]*$/.test(value);
}

function stateRequiredValidator(value, param, _, linkedFieldsValues) {
  return value || (linkedFieldsValues[0] !== 'US' && linkedFieldsValues[0] !== 'CA');
}

function expirationValidator(value, param, linkedPropsValues, datePieces) {
  const now = getDateObject();
  const nowYear = now.getFullYear();
  const nowMonth = now.getMonth();
  const month = datePieces[0] * 1;
  const year = datePieces[1] * 1;

  return !(year < nowYear || (year === nowYear && month < nowMonth + 1));
}

function userBirthdayValidator(value, param, linkedProps) {
  const birthdayValues = linkedProps[0];
  if (!birthdayValues.userBirthMonth && !birthdayValues.userBirthYear) {
    return true;
  }
  return !!value;
}

function cardNumberForTypeValidator(value, param, linkedProps, linkedFields) {
  let cleanValue = (value || '').replace(/\D/g, '');
  cleanValue = (cleanValue || '').replace(/\s/g, '');
  // no type, invalid CC numbr
  const cardType = linkedProps[0] || linkedFields[0];
  if (!cardType) {
    return false;
  }

  const isAmex = cardType === ACCEPTED_CREDIT_CARDS.AMEX;
  const isValidAmex = isAmex && (cleanValue.length === 15 || /[*]{11}\d{4}$/.test(value));
  const isValidNonAmex = !isAmex && (cleanValue.length === 16 || /[*]{12}\d{4}$/.test(value));
  return (
    cleanValue.length === 0 ||
    isValidAmex || // editing amex card
    isValidNonAmex
  ); // editing amex card
}
function cardNumberForNonCCValidator(value, param, linkedProps, linkedFields) {
  const cardType = linkedProps[0] || linkedFields[0];
  const paymentMode = linkedProps[1] || linkedFields[1]; // Payment method Radio button value in spc
  if (
    !linkedProps[2] &&
    paymentMode === CARD_TYPES.CREDIT_CARD &&
    cardType === CARD_TYPES.PLCC_CARD
  ) {
    return false;
  }

  return true;
}

function cardNumberForNonPLCCValidator(value, param, linkedProps, linkedFields) {
  const cardType = linkedProps[0] || linkedFields[0];
  const paymentMode = linkedProps[1] || linkedFields[1]; // Payment method Radio button value in spc
  // if a proper card type has been evaluated but not a plcc
  if (paymentMode === CARD_TYPES.PLCC_CARD && cardType && cardType !== CARD_TYPES.PLCC_CARD) {
    return false;
  }

  return true;
}

function plccEnabledValidator(value, param, linkedProps, linkedFields) {
  const cleanValue = (value || '').replace(/\D/g, '');
  // For PLCC card it was returning false and then UI shows the error, which should not happen.
  // Added PLCC card option for this.
  const cardType = linkedProps[0] || linkedFields[0];
  return (
    !(cleanValue.length > 0 && cardType === 'PLACE CARD' && !linkedProps[1]) ||
    (cardType === 'PLACE CARD' && linkedProps[1])
  );
}

// TODO - Add test case (Ajay Saini)
function numberValidator(value) {
  return /^(?:-?\d+|-?\d{1,3}(?:,\d{3})+)?(?:\.\d+)?$/.test(value);
}

// TODO - Add test case (Ajay Saini)
function lengthValidator(value, length) {
  const len = (value || '').length;
  return len === 0 || len === length;
}

function emailValidator(value) {
  const defaultRegex =
    '^(\\s*)([a-zA-Z0-9_\\-.]+)@((\\[[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.)|(([a-zA-Z0-9-]+\\.)+))([a-zA-Z]{2,4}|[0-9]{1,3})(\\]?)(\\s*)$';
  if (!emailValidationRegex) {
    const storeRef = getStoreRef();
    const state = (storeRef && storeRef.getState()) || {};
    emailValidationRegex = state?.session?.siteDetails?.EMAIL_VALIDATION_REGEX;
  }
  const regex = new RegExp(emailValidationRegex || defaultRegex);
  return regex.test(value);
}
function emailValidatorAcceptingPlus(value) {
  return /^(\s*)([a-zA-Z0-9_\-.+]+)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)|(([a-zA-Z0-9-]+\.)+))([a-zA-Z]{2,4}|[0-9]{1,3})(\]?)(\s*)$/.test(
    value
  );
}
function signUpEmailValidator(value) {
  if ((value || '').length === 0) {
    return true; // otherwise it's always mandatory
  }
  return /^(\s*)([a-zA-Z0-9_\-.]+)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)|(([a-zA-Z0-9-]+\.)+))([a-zA-Z]{2,4}|[0-9]{1,3})(\]?)(\s*)$/.test(
    value
  );
}

function multiEmailsValidator(values) {
  const valuesArr = (values || '').split(',');
  let isValid = true;

  valuesArr.map((value) => {
    const localvalue = value.trim();
    isValid = isValid && (!localvalue || emailValidator(localvalue));
    return isValid;
  });

  return isValid;
}

function matchEmailValidator(value, param, linkedPropsValues, linkedFieldsValues) {
  return (value || '').trim() === (linkedFieldsValues[0] || '').trim();
}

function passwordValidator(value) {
  return /^(?!.*[<>])(?!.*[\s])(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^_&*()?.-])(?=.{8,})/.test(
    value
  );
}

function equalToValidator(value, param, linkedPropsValues, linkedFieldsValues) {
  return value === linkedFieldsValues[0];
}

function legacyPasswordValidator(value) {
  return /^(?=.*[A-Z])(?=.*\d)[A-Za-z\d$@#%^$<>.,!%*?&\-_~`()+={}[\]|:;"'/]{8,}$/g.test(value);
}

function userDateOfBirthValidator(value, param, linkedPropsValues, linkedFieldsValue) {
  if (
    (value !== 'MM' && linkedFieldsValue && linkedFieldsValue[0] === 'YYYY') ||
    (value === 'MM' && linkedFieldsValue && linkedFieldsValue[0] !== 'YYYY')
  ) {
    return false;
  }
  return true;
}

function alphanumericValidator(value) {
  return !value ? true : /^[0-9A-Za-z]{12}$/.test(value);
}

function ssnValidator(value) {
  return /^[0-9]{4}$/.test(value);
}

function dobValidator(value) {
  return ['Mm', 'Dd', 'Yyyy', ''].indexOf(value) === -1;
}

function onlyDigitsValidator(value) {
  return /^\d+$/.test(value);
}

function cvvLengthThreeValidator(value, param, linkedProps, linkedFieldsValues) {
  const cardType = linkedProps[0] || linkedFieldsValues[0];
  return cardType?.toLowerCase() !== ACCEPTED_CREDIT_CARDS.AMEX.toLowerCase()
    ? (value || '').length === 3
    : true;
}

function cvvLengthFourValidator(value, param, linkedProps, linkedFieldsValues) {
  const cardType = linkedProps[0] || linkedFieldsValues[0];
  return cardType?.toLowerCase() === ACCEPTED_CREDIT_CARDS.AMEX.toLowerCase()
    ? (value || '').length === 4
    : true;
}

function eitherRequiredValidator(value, param, linkedPropsValues, linkedFieldsValues) {
  return (value || linkedFieldsValues[0] || '').length > 0;
}

function notEqualToValidator(value, param, linkedPropsValues, linkedFieldsValues) {
  return value !== linkedFieldsValues[0];
}

/**
 * @function - nonSequentialNumberValidator
 *
 * @param {*} value  - value to be validated for having non sequestial numbers
 */
const nonSequentialNumberValidator = (value) => {
  if (!value) {
    return true;
  }

  const isInvalid =
    /^([0-9])(\1\1\1)$/gi.test(value) ||
    '0123456789012'.indexOf(value) > -1 ||
    '9876543210987'.indexOf(value) > -1;
  return !isInvalid;
};

const creditCardDateValidator = (value) => {
  const dateFormat = 'MM/yy';
  const validDate = convertToValidDate(value);
  const expiryDate = convertDateWithSlash(validDate);
  return (
    value &&
    !!isMatch(value, dateFormat) &&
    (value === format(getDateObject(), dateFormat) || !!isFuture(getDateObject(expiryDate)))
  );
};

const age18plusValidator = (defaultAgeGateVal, value) => {
  if (!value) {
    return true;
  }
  const getDefaultAgeGateValue = parseInt(defaultAgeGateVal, 10);
  const defaultAgeGate = Number.isNaN(getDefaultAgeGateValue) ? 18 : getDefaultAgeGateValue;
  const today = getDateObject();
  const birth = getDateObject(value);
  let age = today.getFullYear() - birth.getFullYear();
  const month = today.getMonth() - birth.getMonth();
  if (month < 0 || (month === 0 && today.getDate() < birth.getDate())) {
    // eslint-disable-next-line no-plusplus
    age--;
  }
  return age >= defaultAgeGate;
};

const validatorMethods = {
  required: requiredValidator,
  nonEmpty: nonEmptyValidator,
  minLength: minLengthValidator,
  maxLength: maxLengthValidator,
  phone: phoneValidator,
  zipcode: zipcodeValidator,
  postcode: postalcodeValidator,
  address: addressValidator,
  addressField1: addressField1Validation,
  addressLocation: addressValidator,
  name: nameValidator,
  city: cityNameValidator,
  stateRequired: stateRequiredValidator,
  cardNumberForType: cardNumberForTypeValidator,
  cardNumberForNonCC: cardNumberForNonCCValidator,
  cardNumberForNonPLCC: cardNumberForNonPLCCValidator,
  expiration: expirationValidator,
  plccEnabled: plccEnabledValidator,
  number: numberValidator,
  exactLength: lengthValidator,
  validEmail: emailValidator,
  emailAcceptingPlus: emailValidatorAcceptingPlus,
  emailPattern: emailValidator,
  noCountryZip: noCountryZipValidator,
  noCountryZipMerge: noCountryZipMergeValidator,
  matchEmail: matchEmailValidator,
  password: passwordValidator,
  equalTo: equalToValidator,
  legacyPassword: legacyPasswordValidator,
  email: emailValidator,
  userDateOfBirth: userDateOfBirthValidator,
  alphanumeric: alphanumericValidator,
  ssn: ssnValidator,
  dob: dobValidator,
  eitherRequired: eitherRequiredValidator,
  notEqualTo: notEqualToValidator,
  nonSequentialNumber: nonSequentialNumberValidator,
  cvvNumber: onlyDigitsValidator,
  cvvLengthThree: cvvLengthThreeValidator,
  cvvLengthFour: cvvLengthFourValidator,
  userBirthday: userBirthdayValidator,
  multiEmails: multiEmailsValidator,
  creditCardDate: creditCardDateValidator,
  signUpEmail: signUpEmailValidator,
  age18plus: age18plusValidator,
};

export default validatorMethods;
